import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Plus, Send, Power } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useDocumentDetail } from "./use-documents";
import { StatusBadge } from "../shared/StatusBadge";
import { AddTranslationDialog } from "./AddTranslationDialog";
import { PublishDocumentDialog } from "./PublishDocumentDialog";
import { RetireDocumentDialog } from "./RetireDocumentDialog";
import { documentTypeLabel } from "../shared/document-type-label";

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const detail = useDocumentDetail(id);

  const [addOpen, setAddOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);

  const state = detail.data?.state;
  // Mutation surface (add/edit translations, publish, retire) follows
  // the state machine — RETIRED is fully locked even for admins because
  // editing bytes nobody will sign again pollutes the audit chain.
  // SCHEDULED_RETIRE is still mutable because the doc is still live
  // until its scheduled effectiveUntil passes.
  const isRetired = state === "RETIRED";
  const canMutate = canEdit && !isRetired;
  // Publish only applies to drafts; retire only to currently-live docs
  // (active OR already-scheduled — operator might want to change the date).
  const canPublish = canMutate && state === "DRAFT";
  const canRetire = canMutate && (state === "ACTIVE" || state === "SCHEDULED_RETIRE");

  return (
    <CompliancePageShell
      screenTestId="compliance-document-detail-screen"
      titleKey="compliance.documents.detail.title"
      subtitleKey="compliance.documents.detail.subtitle"
    >
      <div>
        <Link
          to="/compliance/documents"
          data-testid="compliance-document-back-link"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          {t("compliance.documents.detail.back")}
        </Link>
      </div>

      {detail.isLoading && (
        <p className="text-sm text-muted-foreground">{t("compliance.documents.detail.loading")}</p>
      )}

      {detail.isError && (
        <p data-testid="compliance-document-detail-error" className="text-sm text-destructive">
          {t("compliance.documents.detail.loadError")}
        </p>
      )}

      {detail.data && state && (
        <>
          {state === "RETIRED" && (
            <div
              data-testid="compliance-document-retired-banner"
              className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
            >
              <span aria-hidden="true">🔒</span>
              <p>
                {t("compliance.documents.detail.retiredBanner", {
                  at: new Date(detail.data.effectiveUntil ?? "").toLocaleString(),
                })}
              </p>
            </div>
          )}

          {state === "SCHEDULED_RETIRE" && detail.data.effectiveUntil && (
            <div
              data-testid="compliance-document-scheduled-banner"
              className="flex items-start gap-2 rounded-md border border-wander-orange/30 bg-wander-orange/5 p-3 text-sm"
            >
              <span aria-hidden="true">⏳</span>
              <p>
                {t("compliance.documents.detail.scheduledRetireBanner", {
                  at: new Date(detail.data.effectiveUntil).toLocaleString(),
                })}
              </p>
            </div>
          )}

          <section className="space-y-3 rounded-md border border-border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">{documentTypeLabel(detail.data)}</h2>
                  <StatusBadge state={state} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(`compliance.roles.${detail.data.roleScope}`)} · v{detail.data.version}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("compliance.documents.effectiveFromLabel")}{" "}
                  {new Date(detail.data.effectiveFrom).toLocaleString()}
                  {detail.data.effectiveUntil && (
                    <>
                      {" · "}
                      {t("compliance.documents.effectiveUntilLabel")}{" "}
                      {new Date(detail.data.effectiveUntil).toLocaleString()}
                    </>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {canRetire && (
                  <Button
                    variant="outline"
                    data-testid="compliance-retire-document-button"
                    onClick={() => setRetireOpen(true)}
                  >
                    <Power className="mr-2 h-4 w-4" aria-hidden="true" />
                    {state === "SCHEDULED_RETIRE"
                      ? t("compliance.documents.detail.rescheduleRetire")
                      : t("compliance.documents.detail.retire")}
                  </Button>
                )}
                {canPublish && (
                  <Button
                    data-testid="compliance-publish-document-button"
                    onClick={() => setPublishOpen(true)}
                  >
                    <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("compliance.documents.detail.publish")}
                  </Button>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {t("compliance.documents.translations.heading")}
              </h3>
              {canMutate && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="compliance-add-translation-button"
                  onClick={() => setAddOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("compliance.documents.translations.add")}
                </Button>
              )}
            </div>

            {(!detail.data.translations || detail.data.translations.length === 0) && (
              <p
                data-testid="compliance-document-detail-no-translations"
                className="text-sm text-muted-foreground"
              >
                {t("compliance.documents.translations.empty")}
              </p>
            )}

            {detail.data.translations && detail.data.translations.length > 0 && (
              <ul className="space-y-2">
                {detail.data.translations.map((tr) => (
                  <li
                    key={tr.id}
                    data-testid={`compliance-translation-row-${tr.localeCode}`}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                          {tr.localeCode}
                        </code>
                        <p className="font-medium">{tr.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t("compliance.documents.translations.updatedAt", {
                          at: new Date(tr.updatedAt).toLocaleString(),
                        })}
                      </p>
                    </div>
                    <Link
                      to={`/compliance/documents/${detail.data!.id}/translations/${encodeURIComponent(tr.localeCode)}`}
                      data-testid={`compliance-translation-edit-link-${tr.localeCode}`}
                      className="text-sm font-medium text-wander-orange hover:underline"
                    >
                      {canMutate ? t("compliance.actions.edit") : t("compliance.actions.view")} →
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <AddTranslationDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            tosId={detail.data.id}
            existingLocales={detail.data.translations?.map((t) => t.localeCode) ?? []}
          />
          <PublishDocumentDialog
            open={publishOpen}
            onOpenChange={setPublishOpen}
            doc={detail.data}
          />
          <RetireDocumentDialog open={retireOpen} onOpenChange={setRetireOpen} doc={detail.data} />
        </>
      )}
    </CompliancePageShell>
  );
}
